import compact from 'lodash/compact';
import isNil from 'lodash/isNil';
import dayjs, {
  Dayjs,
} from 'utils/dayjs';
import {
  pluralize,
} from 'utils/string';
import {
  isEmptyString,
} from './misc';

type DateParam = number | string | Date | Dayjs;

const allowedFormats = [
  'MM/DD/YYYY',
  'MM/DD/YY',
];

function _formatDate(
  format: string,
  date: DateParam,
  keepLocal: boolean = true,
) {
  const dateObject = dayjs(date);
  return dateObject.isValid() ? dateObject.tz(undefined, keepLocal).format(format) : '';
}

export const formatDate = _formatDate.bind(null, 'M/D/YYYY');
export const formatDateTime = _formatDate.bind(null, 'M/D/YYYY h:mma');
export const formatTime = _formatDate.bind(null, 'h:mma');
export const formatDateOnly = _formatDate.bind(null, 'YYYY-MM-DD');
export const formatDateTimeOffset = _formatDate.bind(null, '');
export const formatDay = _formatDate.bind(null, 'ddd D');
export const formatMonth = _formatDate.bind(null, 'MMMM YYYY');
export const formatYear = _formatDate.bind(null, 'YYYY');
export const formatISODateTime = _formatDate.bind(null, 'YYYY-MM-DDTHH:mm:ss');
export const formatTwoDigitDate = _formatDate.bind(null, 'MM/DD/YYYY');
export const formatToReadableDate = _formatDate.bind(null, 'DD MMM, YYYY');

type DateRange = Date[];
export const getDateRangeFromStringParam = (param: string): DateRange => {
  const [from, to] = param.split('--');
  return [
    dayjs(from).startOf('day').toDate(),
    dayjs(to).endOf('day').toDate(),
  ];
};

const timeUnits: string[] = ['hour', 'minute'];
export function getTimeDifferenceString(from: Dayjs, to: Dayjs): string {
  const timeDiff = [
    to.diff(from, 'hours'),
    to.diff(from, 'minutes') % 60,
  ];

  const timeValues = timeDiff.map((diffValue, index) => (
    diffValue > 0 ? `${pluralize(timeUnits[index], diffValue)}` : null
  ));

  return `(${compact(timeValues).join(' ')})`;
}

export function getTimeRangeString(from: Dayjs, to: Dayjs): string {
  return `${formatTime(from)} - ${formatTime(to)}`;
}

export function getDateRangeString(from: Dayjs, to: Dayjs): string {
  return `${formatDate(from)}${from.isSame(to, 'day') ? '' : ` - ${formatDate(to)}`}`;
}

export function isValidDateRange(range: string | Date | (Date | undefined)[] | null) {
  return Array.isArray(range)
          && !isNil(range)
          && range.length === 2
          && range.every((date) => dayjs(date).isValid());
}

export function parseFlexibleDate(input: string): Dayjs | null {
  let parsedDate = null;

  if (!isEmptyString(input) && typeof input === 'string') {
    const cleanInput = input.trim();

    // Check if input matches MM/DD/YY format specifically
    const twoDigitYearMatch = cleanInput.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2})$/) ?? [];

    const month = parseInt(twoDigitYearMatch[1], 10);
    const day = parseInt(twoDigitYearMatch[2], 10);
    const twoDigitYear = parseInt(twoDigitYearMatch[3], 10);
    const isValid = month > 0 && month <= 12 && day > 0 && day <= 31;

    if (twoDigitYearMatch && isValid) {
      const currentYear = dayjs().year();
      const currentTwoDigit = currentYear % 100;

      const decade = twoDigitYear <= currentTwoDigit ? 2000 : 1900;
      const fullYear = decade + twoDigitYear;

      const tentativeDate = dayjs(`${month}/${day}/${fullYear}`, 'M/D/YYYY', true);
      const isValidDate = tentativeDate.isValid()
                            && tentativeDate.month() === month - 1 // dayjs months are 0-indexed
                            && tentativeDate.date() === day
                            && tentativeDate.year() === fullYear;

      parsedDate = isValidDate ? tentativeDate : null;
    } else {
      // Only allow complete date formats, reject incomplete dates
      const validFormat = allowedFormats.find((format) => {
        const parsed = dayjs(cleanInput, format, true);
        return parsed.isValid();
      });

      // Only parse if it matches one of our allowed complete formats
      if (!isNil(validFormat)) {
        // Additional validation: ensure input has the right number of parts
        const separator = validFormat.includes('/') ? '/' : '-';
        const expectedParts = validFormat.split(separator).length;
        const actualParts = cleanInput.split(separator).length;

        // Also check that the input uses the same separator as the format
        let inputSeparator = '';
        if (cleanInput.includes('/')) {
          inputSeparator = '/';
        } else if (cleanInput.includes('-')) {
          inputSeparator = '-';
        }

        if (expectedParts === actualParts && separator === inputSeparator) {
          const parsed = dayjs(cleanInput, validFormat, true);
          parsedDate = parsed.isValid() ? parsed : null;
        }
      }
    }
  }

  return parsedDate;
}

export function sortVisitsByDate<T extends { visitDateTime?: string | null }>(
  visits: T[] | null | undefined,
  order: 'asc' | 'desc' = 'asc',
): T[] {
  if (!visits) return [];
  return [...visits].sort((a, b) => {
    const timeA = a?.visitDateTime ? new Date(a.visitDateTime).getTime() : 0;
    const timeB = b?.visitDateTime ? new Date(b.visitDateTime).getTime() : 0;
    return order === 'asc' ? timeA - timeB : timeB - timeA;
  });
}

export type CalendarBadgeDateInfo = {
  ccmMonth: string;
  ccmDay: string;
  formattedCcmFullDate: string;
};

export function formatCcmCallDate(
  rawDate?: DateParam | null,
): CalendarBadgeDateInfo {
  const ccmDateObj = dayjs(rawDate);
  const isValid = !isNil(rawDate) && ccmDateObj.isValid();

  return {
    ccmMonth: isValid ? ccmDateObj.format('MMM').toUpperCase() : '',
    ccmDay: isValid ? ccmDateObj.format('D') : '',
    formattedCcmFullDate: isValid ? ccmDateObj.format('ddd, MMM D, YYYY') : '',
  };
}
